import { Anchor, Button, Checkbox } from '@cluey/cluey-components';
import moment from 'moment';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { create } from 'zustand';

import { PATHS } from '.';
import { api } from '../../../../api';
import { ddLogger, ddLoggerLevel } from '../../../../logging';
import ClueyPlusLoader from '../../../../v2/common/ClueyPlusLoader';
import RightColumn from '../RightColumn';
import TwoColumnLayout from '../TwoColumnLayout';
import { BackButton } from '../common';
import type { StudentDetailsForm, StudentEntryForm } from './SignUpStudentDetails';
import Stepper from './Stepper';
import StudentsCard from './StudentsCard';
import type { SignUpUserDetails } from './prepareSignUpPayload';
import useSignout from './useSignout';

const TRIAL_DURATION = 14;

type ReviewDetailsStore = {
  signUpUser: SignUpUserDetails;
  isParent: boolean;
  students: StudentEntryForm[] | null;
  formValues: StudentDetailsForm | null;
  setSignUpUser: (signUpUser: SignUpUserDetails) => void;
  setStudents: (students: StudentEntryForm[]) => void;
};

export const useReviewDetailsStore = create<ReviewDetailsStore>((set) => ({
  signUpUser: null,
  isParent: false,
  students: [],
  formValues: null,
  setFormValues: (formValues) => set({ formValues }),
  setSignUpUser: (signUpUser) => set({ signUpUser }),
  setStudents: (students) => set({ students }),
}));

export const generateMessage = (userType: string, students: StudentEntryForm[]) => {
  if (userType === 'Student') {
    return 'We hope you get enough time to explore and we will send a reminder as the free trial is coming to an end.';
  } else if (userType === 'Parent / Guardian') {
    if (students.length > 2) {
      const otherStudents = students
        .slice(0, students.length - 1)
        .map((student) => student.firstName)
        .join(', ');
      return `We hope ${otherStudents} and ${students[students.length - 1].firstName} get enough time to explore and we will send a reminder as the free trial is coming to an end.`;
    } else if (students.length === 2) {
      return `We hope ${students[0].firstName} and ${students[1].firstName} get enough time to explore and we will send a reminder as the free trial is coming to an end.`;
    } else {
      const student = students[0];
      return `We hope ${student.firstName} gets enough time to explore and we will send a reminder as the free trial is coming to an end.`;
    }
  }
};

const ReviewDetailsPage = () => {
  const history = useHistory();
  const { signUpUser, students } = useReviewDetailsStore();
  const isNZ = signUpUser?.country === 'NZ';
  const privacyPolicyLink = isNZ
    ? 'https://clueylearning.co.nz/privacy-policy/'
    : 'https://clueylearning.com.au/privacy-policy/';
  const { formValues } = useReviewDetailsStore();
  const signout = useSignout();
  const {
    data: accountDetails,
    isLoading: isLoadingAccountDetails,
    error: fetchAccountDetailsError,
  } = api.account.details.useQuery();
  const [agree, setAgree] = useState(false);
  const [termsAndConditionsError, setTermsAndConditionsError] = useState('');
  const estimatedTrialEndDate = moment().add(TRIAL_DURATION, 'days').format('D MMMM YYYY');
  const {
    mutate: signUp,
    isLoading: isSigningUp,
    error: signUpError,
  } = api.clueyPlus.signUp.useMutation({
    onSuccess: () => {
      signout();
      history.push(PATHS.SUCCESS_PATH);
    },
    onError: (error) => {
      ddLogger({
        level: ddLoggerLevel.ERROR,
        label: 'Sign up failed',
        data: { error: (error as Error).message, students, signUpUser, accountDetails },
        error,
      });
    },
  });

  if (!signUpUser || !students) {
    history.replace(PATHS.STUDENT_DETAILS_PATH);
  }

  const handleSubmit = () => {
    if (!agree) {
      setTermsAndConditionsError('Agree to Terms and Conditions to continue');
    } else {
      signUp({
        leadId: accountDetails?.customerSfid,
        countryCode: signUpUser.country as 'AU' | 'NZ',
        students: students.map((student) => ({
          firstName: student.firstName,
          lastName: isStudentSigningUp ? signUpUser.lastName : '[Not provided]',
          state: student.state,
          postcode: student.postCode,
          yearLevel: student.schoolYear,
        })),
        /*
        students: isParent
          ? students.map((student) => ({
              firstName: student.firstName,
              lastName: signUpUser.lastName,
              state: student.state,
              postcode: student.postCode,
              yearLevel: student.schoolYear,
            }))
          : [], // TBD-8028 if the student is signing up for themselves studentDetails should be empty
        */
      });
    }
  };

  const { signingUpFor } = formValues;
  const isStudentSigningUp = signingUpFor === 'Student';
  const message = generateMessage(signingUpFor || 'Student', students);

  const renderLeftColumn = () => {
    if (isLoadingAccountDetails || isSigningUp) {
      return (
        <div className="ml-auto mr-auto box-content flex h-full max-w-[420px] flex-col justify-center px-5">
          <ClueyPlusLoader />
        </div>
      );
    }
    return (
      <div className="ml-auto mr-auto box-content flex h-full max-w-[420px] flex-col px-5">
        <div className="mt-10">
          <BackButton />
        </div>
        <div className="mb-6 mt-6">
          <Stepper steps={5} activeStep={5} />
        </div>
        <h2 className="mb-6 font-display text-4xl font-bold">Final step</h2>
        <p>
          Your Cluey+ free trial will last for {TRIAL_DURATION} days and will end on {estimatedTrialEndDate}. {message}
        </p>
        <p className="mt-4">
          The Cluey+ service is aligned to the Australian Curriculum and optimised for each student.
        </p>
        <div className="mt-7">
          <StudentsCard students={students ?? []} />
        </div>
        <div className="mb-5 mt-10 flex flex-row">
          <div className="h-12 w-12">
            <Checkbox
              id="signup-agree"
              value={agree}
              error={!!termsAndConditionsError}
              onChange={(e) => {
                setAgree(e);
                setTermsAndConditionsError('');
              }}
            />
          </div>
          <label htmlFor="signup-agree">
            I have read and agree to Cluey+
            <Anchor
              className="ml-1"
              href={
                isNZ
                  ? 'https://clueylearning.co.nz/cluey-plus-agreement/'
                  : 'https://clueylearning.com.au/cluey-plus-agreement/'
              }
              size="base"
              target="_blank"
            >
              Terms and Conditions
            </Anchor>
          </label>
        </div>
        <div className="mb-5">
          By continuing, I consent that Cluey collects, uses and discloses my personal information in accordance with
          Cluey's
          <Anchor className="ml-[0.25rem]" href={privacyPolicyLink} size="base" target="_blank">
            Privacy Policy.
          </Anchor>
        </div>
        {termsAndConditionsError && <div className="text-xs text-red-5">{termsAndConditionsError}</div>}
        {fetchAccountDetailsError && <div className="text-xs text-red-5">{fetchAccountDetailsError as string}</div>}
        {signUpError && <div className="text-xs text-red-5">{signUpError as string}</div>}
        <Button size="lg" className="mt-5" onClick={handleSubmit} disabled={!!fetchAccountDetailsError}>
          Start free trial
        </Button>
      </div>
    );
  };
  const renderRightColumn = () => {
    return <RightColumn step={4} />;
  };
  return <TwoColumnLayout renderLeftColumn={renderLeftColumn} renderRightColumn={renderRightColumn} />;
};

export default ReviewDetailsPage;
